import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import { cn } from '../../lib/utils';
import { 
  FilterDropdown, 
  DateFilterDropdown, 
  NumberRangeFilter, 
  BooleanFilter, 
  TextFilter
} from '../FiltersPanel';
import ActiveFiltersDisplay from '../FiltersPanel/ActiveFiltersDisplay';
import FilterPresets from './FilterPresets';
import { DocumentTextIcon, ChevronDownIcon, XIcon } from '../ui/Icons';
import useSWR from 'swr';
import { getFilterPresets, getDefaultFilterPreset } from '../../services/filterPresetsService';

// Unified filter structure
export interface UnifiedFilter {
  type: 'multiSelect' | 'dateRange' | 'numericRange' | 'boolean' | 'text' | 'select';
  // For multiSelect
  values?: string[];
  // For dateRange
  preset?: string;
  from?: string;
  to?: string;
  // For numericRange
  min?: number;
  max?: number;
  // For boolean
  value?: boolean;
  // For text/select
  textValue?: string;
}

export interface FilterGroup {
  name: string;
  label: string;
  icon?: string | React.ReactNode;
  fields: string[];
}

export interface FilterFieldConfig {
  key: string;
  label: string;
  placeholder?: string;
  type?: 'date' | 'number' | 'boolean' | 'text' | 'select';
  getValue: (item: any) => string | undefined;
  options?: string[];
  apiEndpoint?: string; // API endpoint to fetch options for foreign key/select fields
  labelField?: string; // Field name in API response to use as label
}

interface FilterSectionProps {
  // Config - can use either filterFields (from filters.fields) or formFields (from fields)
  config: {
    filterFields?: FilterFieldConfig[]; // From filters.fields
    formFields?: Array<{ name: string; label: string; type: string; options?: any[] }>; // From fields
    filterGroups?: FilterGroup[];
    filterableFields?: string[]; // Optional: manually specify filterable fields
    applyFilter?: (
      items: any[],
      filters: any,
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => any[];
  };
  entityName: string;
  data: any[];
  apiOptionsData?: Record<string, any[]>; // API-fetched options for foreign key fields
  
  // Filter state
  filtersVisible: boolean;
  setFiltersVisible: (visible: boolean) => void;
  unifiedFilters: Record<string, UnifiedFilter>;
  setUnifiedFilters: React.Dispatch<React.SetStateAction<Record<string, UnifiedFilter>>>;
  activeFilterTab?: string;
  setActiveFilterTab?: (tab: string) => void;
  
  // Search
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  searchPlaceholder?: string;
  
  // Filtered data count
  filteredCount?: number;
  
  // Labels
  labels?: {
    searchPlaceholder?: string;
    showFiltersTooltip?: string;
    hideFiltersTooltip?: string;
    allPrefix?: string;
    clearAllText?: string;
    filtersText?: string;
  };
  enableAdvancedSearch?: boolean;
}

const FilterSection = ({
  config,
  entityName,
  data,
  apiOptionsData = {},
  filtersVisible,
  setFiltersVisible,
  unifiedFilters,
  setUnifiedFilters,
  activeFilterTab,
  setActiveFilterTab,
  searchTerm,
  setSearchTerm,
  searchPlaceholder,
  filteredCount,
  labels = {},
  enableAdvancedSearch: enableAdvancedSearchOverride,
}: FilterSectionProps) => {
  const { t } = useTranslation('common');
  const { colors } = useTheme();
  const { appConfig } = useSettings();
  const [showFilterPresets, setShowFilterPresets] = useState(false);
  const [presetsDropdownOpen, setPresetsDropdownOpen] = useState(false);
  const presetsDropdownRef = React.useRef<HTMLDivElement>(null);
  const tableId = entityName.toLowerCase().replace(/\s+/g, '-');
  
  // Check if advanced search is enabled
  const enableAdvancedSearch =
    typeof enableAdvancedSearchOverride === 'boolean'
      ? enableAdvancedSearchOverride
      : appConfig.features.enableAdvancedSearch;

  // Load presets from API using SWR
  const { data: savedFilterPresets = [], mutate: mutatePresets } = useSWR(
    enableAdvancedSearch ? `filter-presets-${tableId}` : null,
    enableAdvancedSearch ? () => getFilterPresets(tableId) : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  // Load default preset on mount
  useEffect(() => {
    const loadDefaultPreset = async () => {
      try {
        const defaultPreset = await getDefaultFilterPreset(tableId);
        if (defaultPreset) {
          setUnifiedFilters(defaultPreset.filters as Record<string, UnifiedFilter>);
        }
      } catch (error) {
        console.error('Error loading default filter preset:', error);
      }
    };
    if (enableAdvancedSearch) {
      loadDefaultPreset();
    }
  }, [tableId, setUnifiedFilters, enableAdvancedSearch]);

  // Refresh presets list
  const onRefreshFilterPresets = () => {
    mutatePresets();
  };

  // Apply filter preset
  const onApplyFilterPreset = (presetId: string | '') => {
    if (presetId === '') {
      setUnifiedFilters({});
      setPresetsDropdownOpen(false);
      return;
    }
    const preset = savedFilterPresets.find((p: any) => p.id.toString() === presetId);
    if (preset) {
      setUnifiedFilters(preset.filters);
      setPresetsDropdownOpen(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (presetsDropdownRef.current && !presetsDropdownRef.current.contains(event.target as Node)) {
        setPresetsDropdownOpen(false);
      }
    };

    if (presetsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [presetsDropdownOpen]);
  
  const defaultLabels = {
    searchPlaceholder: searchPlaceholder || t('filters.searchPlaceholder', { entity: entityName.toLowerCase() }),
    showFiltersTooltip: t('filters.show'),
    hideFiltersTooltip: t('filters.hide'),
    allPrefix: 'All',
    clearAllText: 'Clear All',
    filtersText: t('filters.title'),
    ...labels,
  };

  // Get filter field config by key/name
  const getFilterFieldConfig = (fieldKey: string): FilterFieldConfig | null => {
    // Try filterFields first (from filters.fields)
    if (config.filterFields) {
      const field = config.filterFields.find(f => f.key === fieldKey);
      if (field) return field;
    }
    
    // Try formFields (from fields)
    if (config.formFields) {
      const field = config.formFields.find(f => f.name === fieldKey);
      if (field) {
        return {
          key: field.name,
          label: field.label,
          type: field.type as any,
          getValue: (item: any) => String(item[field.name] || ''),
          options: field.options?.map(opt => typeof opt === 'object' ? String(opt.value || opt.label) : String(opt)),
        };
      }
    }
    
    return null;
  };

  // Get unique filter options for a field
  const getFilterOptions = (fieldKey: string): string[] => {
    const fieldConfig = getFilterFieldConfig(fieldKey);
    
    // If filterField has an API endpoint, use API-fetched options
    if (fieldConfig?.apiEndpoint && apiOptionsData[fieldKey] && apiOptionsData[fieldKey].length > 0) {
      const apiData = apiOptionsData[fieldKey];
      const labelField = fieldConfig.labelField; // Use the configured labelField
      
      return apiData.map((item: any) => {
        // First try the configured labelField, then fall back to common field names
        const label = labelField && item[labelField] !== undefined && item[labelField] !== null
          ? item[labelField]
          : item.as_descr || 
            item.name || 
            item.label || 
            item.description || 
            item.title ||
            item.clientName ||
            item.userDisplayName ||
            `Item #${item.id}`;
        return String(label);
      }).sort();
    }
    
    // Use static options if available
    if (fieldConfig?.options && fieldConfig.options.length > 0) {
      return fieldConfig.options;
    }
    
    // Extract unique values from data using getValue if available
    if (fieldConfig?.getValue) {
      const uniqueValues = Array.from(new Set(
        data.map(item => {
          const value = fieldConfig.getValue(item);
          if (!value || value === '') return null;
          return String(value);
        }).filter(Boolean) as string[]
      ));
      return uniqueValues.sort();
    }
    
    // Fallback: extract directly from data
    const uniqueValues = Array.from(new Set(
      data.map(item => {
        const value = item[fieldKey];
        if (value === null || value === undefined || value === '') return null;
        return String(value);
      }).filter(Boolean) as string[]
    ));
    return uniqueValues.sort();
  };

  // Auto-detect filterable fields
  const getAllFilterableFields = (): FilterFieldConfig[] => {
    // If manually specified, use that
    if (config.filterableFields && Array.isArray(config.filterableFields)) {
      const fields: FilterFieldConfig[] = [];
      config.filterableFields.forEach(key => {
        const field = getFilterFieldConfig(key);
        if (field) fields.push(field);
      });
      return fields;
    }
    
    // Use filterFields if available
    if (config.filterFields && config.filterFields.length > 0) {
      return config.filterFields;
    }
    
    // Auto-detect from formFields
    if (config.formFields) {
      return config.formFields
        .filter(field => {
          if (field.type === 'hidden') return false;
          if (field.type === 'select' || field.type === 'date' || field.type === 'number') return true;
          if (field.type === 'switch' || field.type === 'boolean' || field.type === 'checkbox') return true;
          if (field.type === 'textarea') return true;
          
          // For text fields, only include if reasonable number of unique values
          if (field.type === 'text' || field.type === 'email' || field.type === 'tel' || field.type === 'url') {
            const uniqueValues = getFilterOptions(field.name);
            return uniqueValues.length > 1 && uniqueValues.length <= 20;
          }
          
          return false;
        })
        .map(field => ({
          key: field.name,
          label: field.label,
          type: field.type as any,
          getValue: (item: any) => String(item[field.name] || ''),
          options: field.options?.map(opt => typeof opt === 'object' ? String(opt.value || opt.label) : String(opt)),
        }));
    }
    
    return [];
  };

  // Get filterable fields for current tab
  const getFilterableFieldsForTab = (tabName?: string): FilterFieldConfig[] => {
    const allFilterableFields = getAllFilterableFields();
    
    if (!config.filterGroups || config.filterGroups.length === 0) {
      return allFilterableFields;
    }
    
    const tab = tabName || activeFilterTab || config.filterGroups[0].name;
    const tabConfig = config.filterGroups.find(group => group.name === tab);
    
    if (!tabConfig) {
      return allFilterableFields;
    }
    
    return allFilterableFields.filter(field => 
      tabConfig.fields.includes(field.key)
    );
  };

  // Get active filter count for a tab
  const getActiveFilterCountForTab = (tabName: string): number => {
    if (!config.filterGroups || config.filterGroups.length === 0) {
      return Object.keys(unifiedFilters).length;
    }
    
    const tabConfig = config.filterGroups.find(group => group.name === tabName);
    if (!tabConfig) return 0;
    
    let count = 0;
    tabConfig.fields.forEach(fieldName => {
      const filter = unifiedFilters[fieldName];
      if (filter && isFilterActive(filter)) {
        count++;
      }
    });
    
    return count;
  };

  // Check if a filter is active
  const isFilterActive = (filter: UnifiedFilter): boolean => {
    switch (filter.type) {
      case 'multiSelect':
        return filter.values !== undefined && filter.values.length > 0;
      case 'dateRange':
        return !!(filter.preset && filter.preset !== 'all' && (filter.from || filter.to));
      case 'numericRange':
        return filter.min !== undefined || filter.max !== undefined;
      case 'boolean':
        return filter.value !== undefined;
      case 'text':
        return filter.textValue !== undefined && filter.textValue.trim() !== '';
      case 'select':
        return filter.textValue !== undefined && filter.textValue !== '';
      default:
        return false;
    }
  };

  // Update unified filter
  const updateUnifiedFilter = (fieldName: string, filter: UnifiedFilter | null) => {
    setUnifiedFilters(prev => {
      const newFilters = { ...prev };
      
      if (!filter || !isFilterActive(filter)) {
        delete newFilters[fieldName];
      } else {
        newFilters[fieldName] = filter;
      }
      
      return newFilters;
    });
  };

  // Remove unified filter
  const removeUnifiedFilter = (fieldName: string) => {
    setUnifiedFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[fieldName];
      return newFilters;
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setUnifiedFilters({});
  };

  // Get active filter count
  const activeFilterCount = Object.keys(unifiedFilters).filter(key => 
    isFilterActive(unifiedFilters[key])
  ).length;

  // Render top controls (search + filter toggle + presets dropdown)
  const renderTopControls = () => {
    const filterableFields = getAllFilterableFields();
    const hasFilters = filterableFields.length > 0;
    const activePreset = savedFilterPresets.find(preset => 
      JSON.stringify(preset.filters) === JSON.stringify(unifiedFilters)
    );

    return (
      <div className="flex items-center gap-2 flex-1">
        {/* Search Input */}
        <div className="relative flex-1" style={{ maxWidth: '60%' }}>
          <input
            type="text"
            placeholder={defaultLabels.searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={cn(
              'w-full pl-10 py-2.5 text-sm rounded-lg border transition-all',
              searchTerm.trim() ? 'pr-10' : 'pr-4',
              colors.surface,
              colors.border,
              colors.text,
              'focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
            )}
            aria-label={defaultLabels.searchPlaceholder}
          />
          <svg
            className={cn('absolute left-3 top-3.5 w-4 h-4 pointer-events-none', colors.textMuted)}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchTerm.length > 0 && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className={cn(
                'absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md transition-colors',
                colors.textMuted,
                colors.interactiveHover
              )}
              title="Clear search"
              aria-label="Clear search"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Filter Presets Dropdown - Only show if advanced search is enabled */}
        {hasFilters && enableAdvancedSearch && (
          <div className="relative" ref={presetsDropdownRef}>
            <button
              onClick={() => setPresetsDropdownOpen(!presetsDropdownOpen)}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg border transition-all min-w-[180px] justify-between shadow-sm',
                colors.surface,
                colors.border,
                colors.text,
                `${colors.interactiveHover}`
              )}
              title="Quick filter presets"
            >
              <div className="flex items-center gap-2">
                <svg className={`w-4 h-4 ${colors.textSecondary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="truncate">
                  {activePreset ? activePreset.name : 'All Filters'}
                </span>
                {activeFilterCount > 0 && !activePreset && (
                  <span className={`ml-1 px-1.5 py-0.5 text-xs ${colors.info} rounded`}>
                    {activeFilterCount}
                  </span>
                )}
              </div>
              <ChevronDownIcon className={cn('w-4 h-4', colors.textMuted)} />
            </button>

            {presetsDropdownOpen && (
              <div className={cn(
                'absolute top-full left-0 mt-1 z-50 min-w-[240px] rounded-lg shadow-lg border overflow-hidden',
                colors.background,
                colors.border
              )}>
                {/* Clear All Option */}
                <button
                  onClick={() => onApplyFilterPreset('')}
                  className={cn(
                    `w-full px-3 py-2 text-sm text-left ${colors.surfaceHover} ${colors.interactiveHover} transition-colors flex items-center justify-between`,
                    activeFilterCount === 0 ? colors.info : ''
                  )}
                >
                  <div className="flex items-center gap-2">
                    <XIcon className={`w-4 h-4 ${colors.textSecondary}`} />
                    <span>All Filters</span>
                  </div>
                  {activeFilterCount === 0 && (
                    <svg className={`w-4 h-4 ${colors.textSecondary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>

                {savedFilterPresets.length > 0 && (
                  <div className={cn('border-t', colors.border)} />
                )}

                {/* Saved Presets */}
                {savedFilterPresets.map((preset: any) => {
                  const isActive = JSON.stringify(preset.filters) === JSON.stringify(unifiedFilters);
                  const isDefault = preset.isDefault;
                  
                  return (
                    <button
                      key={preset.id}
                      onClick={() => onApplyFilterPreset(preset.id.toString())}
                      className={cn(
                        `w-full px-3 py-2 text-sm text-left ${colors.surfaceHover} ${colors.interactiveHover} transition-colors`,
                        isActive ? colors.info : ''
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <svg className="w-4 h-4 text-indigo-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                          </svg>
                          <div className="flex flex-col flex-1 min-w-0">
                            <span className="text-sm font-medium truncate">{preset.name}</span>
                            <span className={`text-xs ${colors.textMuted}`}>
                              {(() => {
                                const activeFilters = Object.entries(preset.filters || {}).filter(([key, filterConfig]) => {
                                  const config = filterConfig as UnifiedFilter;
                                  if (config.type === 'multiSelect') return config.values && config.values.length > 0;
                                  if (config.type === 'dateRange') return config.from || config.to || config.preset;
                                  if (config.type === 'numericRange') return config.min !== undefined || config.max !== undefined;
                                  if (config.type === 'boolean') return config.value !== undefined;
                                  if (config.type === 'text') return config.textValue;
                                  return config.textValue;
                                });
                                return activeFilters.length === 1 ? '1 filter' : `${activeFilters.length} filters`;
                              })()}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {isDefault && (
                            <span className={`px-1.5 py-0.5 text-xs ${colors.success} rounded`}>
                              Default
                            </span>
                          )}
                          {isActive && (
                            <svg className={`w-4 h-4 ${colors.primaryText}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Filter Toggle Button - Only show if advanced search is enabled */}
        {hasFilters && enableAdvancedSearch && (
          <button
            onClick={() => setFiltersVisible(!filtersVisible)}
            className={cn(
              'relative inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-all shadow-sm',
              filtersVisible
                ? `${colors.primary} ${colors.primaryText} shadow-lg`
                : `${colors.surface} ${colors.border} ${colors.text} ${colors.interactiveHover}`
            )}
            title={filtersVisible ? defaultLabels.hideFiltersTooltip : defaultLabels.showFiltersTooltip}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span className="font-medium">
              {filtersVisible ? 'Hide Filters' : 'Show Filters'}
            </span>
            
            {/* Active filters indicator */}
            {activeFilterCount > 0 && (
              <div className={cn(
                'flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold',
                filtersVisible
                  ? `bg-white/20 ${colors.primaryText}`
                  : `${colors.accent} ${colors.primaryText} shadow-sm`
              )}>
                {activeFilterCount}
              </div>
            )}
            
            <svg
              className={cn('w-3 h-3 transition-transform', filtersVisible ? 'rotate-180' : '')}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
      </div>
    );
  };

  // Render filter panels
  const renderFilterPanels = () => {
    const filterableFields = getAllFilterableFields();
    
    // Don't show filter panels if advanced search is disabled
    if (!enableAdvancedSearch || !filtersVisible || filterableFields.length === 0) {
      return null;
    }

    const fieldsForTab = getFilterableFieldsForTab();
    const currentTab = activeFilterTab || (config.filterGroups?.[0]?.name);

    return (
      <div className={cn(
        'px-2 mb-2 rounded-lg border transition-all duration-300',
        activeFilterCount > 0
          ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800'
          : `${colors.surface} ${colors.border}`
      )}>
        {/* Active Filters Display */}
        {activeFilterCount > 0 && (
          <div className="p-3">
            <ActiveFiltersDisplay
              unifiedFilters={unifiedFilters}
              filterFields={getAllFilterableFields()}
              onRemoveFilter={(key) => {
                setUnifiedFilters((prev) => {
                  const newFilters = { ...prev };
                  delete newFilters[key];
                  return newFilters;
                });
              }}
              onClearAll={clearAllFilters}
              filteredCount={filteredCount}
            />
          </div>
        )}

        {/* Filter Header */}
        <div className={`p-4 border-b ${colors.border}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className={cn('w-4 h-4', colors.text)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <h3 className={cn('text-sm font-semibold', colors.text)}>{defaultLabels.filtersText}</h3>
              
              {activeFilterCount > 0 && (
                <span className={cn(`text-xs ${colors.primaryText} px-2 py-1 rounded-full font-medium`, colors.primary)}>
                  {activeFilterCount}
                </span>
              )}
              
              {/* Filter Presets Button */}
              <div className="relative">
                <button
                  onClick={() => setShowFilterPresets(!showFilterPresets)}
                  className={cn(
                    'inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all duration-200 shadow-sm hover:shadow-md group',
                    colors.text,
                    colors.surface,
                    colors.border,
                    `${colors.interactiveHover}`,
                    `${colors.hoverBorder}`
                  )}
                  title="Filter presets"
                >
                  <DocumentTextIcon className={cn('w-3.5 h-3.5 transition-colors', colors.textSecondary, `group-${colors.hoverText}`)} />
                  <span className="font-medium">Presets</span>
                  <ChevronDownIcon className={cn('w-3 h-3 transition-transform duration-200', showFilterPresets ? 'rotate-180' : '', colors.textSecondary)} />
                </button>
                
                <FilterPresets
                  currentFilters={unifiedFilters}
                  onApplyFilters={(filters) => setUnifiedFilters(filters)}
                  onPresetsChange={onRefreshFilterPresets}
                  tableId={tableId}
                  entityName={entityName}
                  isOpen={showFilterPresets}
                  onClose={() => setShowFilterPresets(false)}
                  data={data}
                  applyFilter={config.applyFilter}
                  allFilterKeys={config.filterFields?.map(f => f.key) || []}
                  textFilterKeys={config.filterFields?.filter(f => f.type === 'text').map(f => f.key) || []}
                />
              </div>
              
              {/* Clear All Button */}
              {activeFilterCount > 0 && (
                <button
                  onClick={clearAllFilters}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border transition-all shadow-sm hover:shadow-md',
                    colors.textSecondary,
                    colors.surface,
                    colors.border,
                    `${colors.interactiveHover}`
                  )}
                  title="Clear all active filters"
                >
                  <XIcon className="w-3 h-3" />
                  <span>{defaultLabels.clearAllText}</span>
                </button>
              )}
            </div>

            {/* Filter Group Tabs */}
            {config.filterGroups && config.filterGroups.length > 0 && setActiveFilterTab && (
              <>
                {/* Desktop Tabs */}
                <div className={`hidden md:flex items-center gap-1 ${colors.surfaceHover} p-1 rounded-lg`}>
                  {config.filterGroups.map((group) => {
                    const filterCount = getActiveFilterCountForTab(group.name);
                    const isActive = currentTab === group.name;
                    
                    return (
                      <button
                        key={group.name}
                        onClick={() => setActiveFilterTab(group.name)}
                        className={cn(
                          'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                          isActive
                            ? `${colors.primary} text-white shadow-sm`
                            : `${colors.textSecondary} ${colors.interactiveHover}`
                        )}
                      >
                        {group.icon && (
                          <span className="text-sm">
                            {typeof group.icon === 'string' ? group.icon : group.icon}
                          </span>
                        )}
                        <span>{group.label}</span>
                        {filterCount > 0 && (
                          <span className={cn(
                            'inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full',
                            isActive
                              ? `bg-white bg-opacity-20 ${colors.primaryText}`
                              : `${colors.primary} ${colors.primaryText}`
                          )}>
                            {filterCount}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Mobile Dropdown */}
                <div className="md:hidden relative">
                  <select
                    value={currentTab}
                    onChange={(e) => setActiveFilterTab(e.target.value)}
                    className={cn(
                      'appearance-none pl-3 pr-8 py-2 text-xs font-medium rounded-lg border focus:outline-none focus:ring-2',
                      colors.surface,
                      colors.border,
                      colors.text
                    )}
                  >
                    {config.filterGroups.map((group) => {
                      const filterCount = getActiveFilterCountForTab(group.name);
                      return (
                        <option key={group.name} value={group.name}>
                          {group.icon && typeof group.icon === 'string' ? group.icon + ' ' : ''}
                          {group.label}
                          {filterCount > 0 ? ` (${filterCount})` : ''}
                        </option>
                      );
                    })}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg className={cn('w-4 h-4', colors.textSecondary)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Filter Content */}
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {fieldsForTab.map((field) => {
              const fieldKey = field.key; // Capture field key in a const to ensure stable reference
              const filter = unifiedFilters[fieldKey];
              const isActive = filter && isFilterActive(filter);
              const options = getFilterOptions(fieldKey);
              const fieldType = field.type || 'text';

              return (
                <div
                  key={fieldKey}
                  className={cn(
                    'p-3 rounded-lg border transition-all duration-200',
                    isActive
                      ? `${colors.surface} ${colors.border} shadow-sm ring-1 ${colors.border}`
                      : `${colors.surfaceHover} ${colors.border}`
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <label className={cn('text-sm font-medium', colors.text)}>
                      {field.label}
                    </label>
                    {isActive && (
                      <button
                        onClick={() => removeUnifiedFilter(fieldKey)}
                        className={`h-5 w-5 p-0 ${colors.textMuted} hover:text-red-500 transition-colors`}
                        title="Clear filter"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Render appropriate filter component based on field type */}
                  {fieldType === 'date' ? (
                    <DateFilterDropdown
                      label=""
                      selectedValue={filter?.preset || null}
                      onFilterChange={(value, range) => {
                        if (value === null || !range) {
                          updateUnifiedFilter(fieldKey, null);
                        } else {
                          updateUnifiedFilter(fieldKey, {
                            type: 'dateRange',
                            preset: value,
                            from: range.start ? range.start.toISOString().split('T')[0] : undefined,
                            to: range.end ? range.end.toISOString().split('T')[0] : undefined,
                          });
                        }
                      }}
                      placeholder={defaultLabels.allPrefix + ' ' + field.label}
                    />
                  ) : fieldType === 'number' ? (
                    <NumberRangeFilter
                      label=""
                      selectedRange={
                        filter?.type === 'numericRange'
                          ? { min: filter.min || null, max: filter.max || null }
                          : null
                      }
                      onFilterChange={(range) => {
                        if (!range || (range.min === null && range.max === null)) {
                          updateUnifiedFilter(fieldKey, null);
                        } else {
                          updateUnifiedFilter(fieldKey, {
                            type: 'numericRange',
                            min: range.min || undefined,
                            max: range.max || undefined,
                          });
                        }
                      }}
                      placeholder={defaultLabels.allPrefix + ' ' + field.label}
                    />
                  ) : fieldType === 'boolean' ? (
                    <BooleanFilter
                      label=""
                      selectedValue={
                        filter?.type === 'boolean' ? filter.value ?? null : null
                      }
                      onFilterChange={(value) => {
                        if (value === null) {
                          updateUnifiedFilter(fieldKey, null);
                        } else {
                          updateUnifiedFilter(fieldKey, {
                            type: 'boolean',
                            value: value,
                          });
                        }
                      }}
                      placeholder={defaultLabels.allPrefix + ' ' + field.label}
                      trueLabel={field.options?.[0] || 'Yes'}
                      falseLabel={field.options?.[1] || 'No'}
                    />
                  ) : fieldType === 'text' && options.length > 20 ? (
                    <TextFilter
                      label=""
                      value={filter?.type === 'text' ? filter.textValue || '' : ''}
                      onFilterChange={(value) => {
                        if (!value || value.trim() === '') {
                          updateUnifiedFilter(fieldKey, null);
                        } else {
                          updateUnifiedFilter(fieldKey, {
                            type: 'text',
                            textValue: value.trim(),
                          });
                        }
                      }}
                      placeholder={`Search ${field.label.toLowerCase()}...`}
                    />
                  ) : (
                    <FilterDropdown
                      key={`filter-${fieldKey}`}
                      label=""
                      options={options}
                      selectedValues={
                        filter?.type === 'multiSelect' ? filter.values || [] : []
                      }
                      onFilterChange={(values) => {
                        if (values.length === 0) {
                          updateUnifiedFilter(fieldKey, null);
                        } else {
                          updateUnifiedFilter(fieldKey, {
                            type: 'multiSelect',
                            values: values,
                          });
                        }
                      }}
                      placeholder={defaultLabels.allPrefix + ' ' + field.label}
                      getValue={field.getValue || ((item: any) => String(item[fieldKey] || ''))}
                      data={data}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return {
    topControls: renderTopControls(),
    filterPanels: renderFilterPanels(),
  };
};

export default FilterSection;

